import {
	Balloon,
	ToleranceRow,
	ToleranceRowKind,
	ToleranceTable,
} from '../interfaces/Ballooning';
import { markBulkEdit, markIndividualEdit } from './balloonReview';
import { nominalDecimals } from './nominal';

// Mirrors server/fastlane/ballooning/services/tolerance_table.py; keep the lookup rules in sync.

export const ROW_KIND_LABELS: Record<ToleranceRowKind, string> = {
	linear_range: 'Size range',
	linear_decimal: 'Decimal places',
	linear_default: 'All linear',
	angular: 'Angular',
	thread_depth: 'Thread depth',
};

const TEXT_ONLY_TYPES = ['Engraving', 'Surface Finish', 'Other', 'Thread'];

type Nominal = string | number | null | undefined;

export const hasRows = (table: ToleranceTable | null | undefined): table is ToleranceTable =>
	!!table && Array.isArray(table.rows) && table.rows.length > 0;

const isBlank = (value: unknown) => value === null || value === undefined || value === '';

const toNumber = (value: Nominal): number | null => {
	if (isBlank(value)) return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

/** Digits after the decimal point, from printed text or the value itself (trailing zeros lost). */
export const decimalPlaces = (value: Nominal): number | null => {
	if (isBlank(value)) return null;
	const text = typeof value === 'number' ? String(value) : String(Number(value));
	const match = /\d*\.(\d+)/.exec(text);
	if (match) return match[1].length;
	return /\d/.test(text) ? 0 : null;
};

const isRanged = (row: ToleranceRow) => !isBlank(row.min) && !isBlank(row.max);

const pick = (
	rows: ToleranceRow[],
	value: number,
	places: number | null,
	useRanges = true
): ToleranceRow | null => {
	const ranged = rows.filter(isRanged);
	if (useRanges && ranged.length) {
		const lowest = Math.min(...ranged.map((r) => r.min as number));
		// ISO 2768 style: over min, up to and including max; the lowest range includes its min.
		const hit = ranged.find((r) => {
			const lo = r.min as number;
			const hi = r.max as number;
			return (value === lo && lo === lowest) || (lo < value && value <= hi);
		});
		if (hit) return hit;
	}
	if (places !== null) {
		const hit = rows.find((r) => r.places === places && isBlank(r.min));
		if (hit) return hit;
	}
	return rows.find((r) => isBlank(r.min) && isBlank(r.places)) ?? null;
};

/** `writtenPlaces` is how many places the nominal was written with, when known. */
export const findToleranceRow = (
	table: ToleranceTable | null | undefined,
	nominal: Nominal,
	isAngular = false,
	writtenPlaces?: number | null
): ToleranceRow | null => {
	if (!hasRows(table)) return null;
	const n = toNumber(nominal);
	if (n === null) return null;
	const value = Math.abs(n);
	const places = writtenPlaces ?? decimalPlaces(nominal);
	const ofKind = (kind: ToleranceRowKind) => table.rows.filter((r) => r.kind === kind);
	if (isAngular) {
		const angular = ofKind('angular');
		// A lone "X.X° ±.5°" is the drawing's only angle tolerance, whatever the angle's places.
		const lone = angular.length === 1 && isBlank(angular[0].min) ? angular[0] : null;
		return pick(angular, value, places) ?? lone;
	}
	return (
		pick(ofKind('linear_range'), value, null) ??
		(places !== null ? pick(ofKind('linear_decimal'), value, places, false) : null) ??
		ofKind('linear_default')[0] ??
		null
	);
};

// A countersink's angle keeps gdt_type Countersink; its drawing text has the degree sign.
export const isAngularBalloon = (balloon: Pick<Balloon, 'gdt_type' | 'ai_raw_text'>) =>
	balloon.gdt_type === 'Angular Dimension' ||
	(balloon.gdt_type === 'Countersink' && !!balloon.ai_raw_text?.includes('°'));

/** Balloons whose nominal the table can tolerance (Keyence rows carry no measurement_type). */
export const acceptsTableTolerance = (
	balloon: Pick<Balloon, 'measurement_type' | 'nominal'>
) => !TEXT_ONLY_TYPES.includes(balloon.measurement_type) && !isBlank(balloon.nominal);

const sameNumber = (a: unknown, b: unknown) => {
	if (isBlank(a) && isBlank(b)) return true;
	if (isBlank(a) || isBlank(b)) return false;
	return Number(a) === Number(b);
};

const tolerancesFor = (balloon: Balloon, table: ToleranceTable | null | undefined) => {
	const row = findToleranceRow(
		table,
		balloon.nominal,
		isAngularBalloon(balloon),
		balloon.nominal_decimals
	);
	return { plus: row ? row.plus : null, minus: row ? row.minus : null };
};

/**
 * Re-tolerance every table-sourced balloon after the table changed. Hand-entered and
 * drawing tolerances are left alone. Changed rows are marked as bulk edits, so the save
 * does not count as a review of AI balloons.
 */
export const applyTableToBalloons = (
	balloons: Balloon[],
	table: ToleranceTable | null | undefined
): { balloons: Balloon[]; changed: number } => {
	let changed = 0;
	const next = balloons.map((balloon) => {
		if (balloon.tolerance_source !== 'table' || !acceptsTableTolerance(balloon)) {
			return balloon;
		}
		const { plus, minus } = tolerancesFor(balloon, table);
		if (sameNumber(plus, balloon.tolerance_plus) && sameNumber(minus, balloon.tolerance_minus)) {
			return balloon;
		}
		changed += 1;
		return markBulkEdit({
			...balloon,
			tolerance_plus: plus as unknown as string,
			tolerance_minus: minus as unknown as string,
		});
	});
	return { balloons: next, changed };
};

/** Balloons applyTableToBalloons would change, without changing them. */
export const countAffectedByTable = (
	balloons: Balloon[],
	table: ToleranceTable | null | undefined
) => applyTableToBalloons(balloons, table).changed;

/**
 * A user typed a nominal. Table-sourced and not-yet-sourced balloons take their
 * tolerance from the table; drawing and hand-entered tolerances are kept.
 */
export const applyNominalEdit = (
	balloon: Balloon,
	nominal: number | string | null,
	table: ToleranceTable | null | undefined
): Balloon => {
	const withNominal = {
		...balloon,
		nominal: nominal as unknown as string,
		nominal_decimals: nominalDecimals(nominal),
	};
	const source = balloon.tolerance_source;
	if (source === 'drawing' || source === 'manual') {
		return markIndividualEdit(withNominal);
	}
	const { plus, minus } = tolerancesFor(withNominal, table);
	return markIndividualEdit({
		...withNominal,
		tolerance_plus: plus as unknown as string,
		tolerance_minus: minus as unknown as string,
		tolerance_source: hasRows(table) ? 'table' : null,
	});
};

const fmt = (n: number | null | undefined) => (isBlank(n) ? '?' : String(n));

export const formatTolerance = (row: Pick<ToleranceRow, 'plus' | 'minus'>) =>
	row.plus === row.minus ? `±${fmt(row.plus)}` : `+${fmt(row.plus)} / -${fmt(row.minus)}`;

export const describeRow = (row: ToleranceRow): string => {
	const tol = formatTolerance(row);
	const deg = row.kind === 'angular' ? '°' : '';
	if (isRanged(row)) return `${ROW_KIND_LABELS[row.kind]} ${fmt(row.min)}–${fmt(row.max)}${deg}: ${tol}${deg}`;
	if (!isBlank(row.places)) {
		const pattern = row.places ? `X.${'X'.repeat(row.places as number)}` : 'X';
		return `${ROW_KIND_LABELS[row.kind]} ${pattern}${deg}: ${tol}${deg}`;
	}
	return `${ROW_KIND_LABELS[row.kind]}: ${tol}${deg}`;
};

export const TABLE_SOURCE_LABELS: Record<ToleranceTable['source'], string> = {
	pb: 'From the order (PB)',
	ai: 'Read from the drawing (AI)',
	manual: 'Entered by hand',
};
